import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { VariableSizeList } from 'react-window';
import moment from 'moment-timezone';
import { EVENT_COLOR, EVENT_LABEL } from '../../../../component/mapTripReport/mapColors';
import OdEcoPill from './OdEcoPill';
import { compactDuration, toKm, UNKNOWN_PLACE } from '../odAnalyticsUtils';

/** Row heights. Day header is one line; a trip row is four plus its rule. */
const DAY_HEIGHT = 34;
const TRIP_HEIGHT = 116;

/** As many event dots as fleetconsole shows before the row starts to wrap. */
const MAX_DOTS = 6;

/** Idle shorter than this is rounding, not a stop worth a line in a narrow row. */
const IDLE_FLOOR_SEC = 60;

/** "2 harsh braking · 1 overspeeding" for the dot strip's tooltip. */
const summariseEvents = (types) => {
    const counts = new Map();
    types.forEach((type) => counts.set(type, (counts.get(type) || 0) + 1));
    return [...counts]
        .map(([type, count]) => `${count} ${(EVENT_LABEL[type] || type).toLowerCase()}`)
        .join(' · ');
};

/**
 * The Route Map trip list, following fleetconsole's Route Atlas rail.
 *
 * Trips are sectioned under a day heading carrying that day's trip count and distance, so each row
 * needs only its clock time — the date used to repeat on every row, which is the one thing that
 * does not distinguish one trip from the next.
 *
 * Virtualised, and sized from its own container rather than hardcoded pixel dimensions — the old
 * TripReportList fixed 396x388 and would not follow the viewport.
 */
const TripRail = (props) => {
    const {
        trips,
        events,
        selectedTripId,
        onSelectTrip,
        onHoverTrip,
        timezone,
        totalCount,
    } = props;

    const listWrapRef = useRef(null);
    const listRef = useRef(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const node = listWrapRef.current;
        if (!node) return undefined;

        const measure = () => {
            setSize({ width: node.clientWidth, height: node.clientHeight });
        };
        measure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }

        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const zone = timezone || 'UTC';
    const list = trips || [];

    /**
     * Day headings interleaved with trips, as one flat array the virtualiser can index.
     *
     * A heading is emitted whenever the day *changes* rather than once per calendar day, so the
     * sections follow whatever order the parent sorted the trips into instead of imposing one.
     */
    const rows = useMemo(() => {
        const dated = list.map((trip, index) => ({
            trip,
            no: index + 1,
            day: moment.tz(trip.startTime, zone).format('DD-MM-YYYY'),
        }));

        const totals = new Map();
        dated.forEach(({ trip, day }) => {
            const bucket = totals.get(day) || { count: 0, km: 0 };
            bucket.count += 1;
            bucket.km += toKm(trip.totalDistance);
            totals.set(day, bucket);
        });

        const out = [];
        let lastDay = null;
        dated.forEach((entry) => {
            if (entry.day !== lastDay) {
                lastDay = entry.day;
                out.push({ kind: 'day', day: entry.day, ...totals.get(entry.day) });
            }
            out.push({ kind: 'trip', ...entry });
        });
        return out;
    }, [list, zone]);

    const eventsByTrip = useMemo(() => {
        const map = new Map();
        (events || []).forEach((row) => {
            if (!row?.tripId) return;
            const bucket = map.get(row.tripId);
            if (bucket) bucket.push(row.type);
            else map.set(row.tripId, [row.type]);
        });
        return map;
    }, [events]);

    // VariableSizeList caches every measured offset, so a changed row set has to invalidate it or
    // the list keeps scrolling to yesterday's positions.
    useEffect(() => {
        if (listRef.current) listRef.current.resetAfterIndex(0);
    }, [rows]);

    /**
     * Keep the selected trip in view — the selection often comes from somewhere else entirely (a
     * deck row, a click on the map), and a highlighted row 40 rows down a virtualised list is not
     * rendered at all, let alone visible. 'smart' leaves an already-visible row where it is rather
     * than yanking the list on every re-select. Runs after the resetAfterIndex above, which has to
     * land first or the scroll targets stale offsets.
     *
     * `size.height` is a dependency because there is no list to scroll until the container has
     * measured — a trip selected before the rail mounts would otherwise never be scrolled to.
     */
    useEffect(() => {
        if (!selectedTripId) return;
        const index = rows.findIndex(
            (row) => row.kind === 'trip' && row.trip.startTraceId === selectedTripId,
        );
        if (index >= 0) listRef.current?.scrollToItem?.(index, 'smart');
    }, [selectedTripId, rows, size.height]);

    const itemSize = useCallback(
        (index) => (rows[index]?.kind === 'day' ? DAY_HEIGHT : TRIP_HEIGHT),
        [rows],
    );

    const renderRow = useCallback(({ index, style }) => {
        const row = rows[index];
        if (!row) return null;

        if (row.kind === 'day') {
            return (
                <div style={style} className="od-rail-day">
                    <span>{row.day}</span>
                    <span className="od-rail-day-meta">
                        {`${row.count} trip${row.count === 1 ? '' : 's'} · ${row.km.toFixed(1)} km`}
                    </span>
                </div>
            );
        }

        const { trip, no } = row;
        const isSelected = trip.startTraceId === selectedTripId;
        const types = eventsByTrip.get(trip.startTraceId) || [];
        const idleSec = Math.round((Number(trip.idlingRatio) || 0) * (Number(trip.totalDuration) || 0));
        const clock = (value) => (value ? moment.tz(value, zone).format('HH:mm') : '—');

        return (
            <div style={style}>
                <button
                  type="button"
                  className="od-rail-item"
                  aria-pressed={isSelected}
                  onClick={() => onSelectTrip(trip)}
                  onMouseEnter={() => onHoverTrip(trip.startTraceId)}
                  onMouseLeave={() => onHoverTrip(null)}
                  onFocus={() => onHoverTrip(trip.startTraceId)}
                  onBlur={() => onHoverTrip(null)}
                >
                    <div className="od-rail-item-top">
                        <span className="od-rail-item-no">{`#${no}`}</span>
                        {trip.assetName && (
                            <span className="od-rail-item-asset">{trip.assetName}</span>
                        )}
                        <span className="od-rail-item-spacer" />
                        <span className="od-rail-item-distance">{`${toKm(trip.totalDistance)} km`}</span>
                    </div>

                    <div className="od-rail-item-line">
                        <span className="od-rail-item-when">
                            {`${clock(trip.startTime)}–${clock(trip.endTime)}`}
                        </span>
                        <span className="od-rail-item-meta">
                            {compactDuration(trip.totalDuration)}
                            {idleSec > IDLE_FLOOR_SEC ? ` · idle ${compactDuration(idleSec)}` : ''}
                        </span>
                    </div>

                    {/* Marks on their own row. Sharing the clock's line cost the rail width it
                        does not have: the dots and the pill are fixed-size, so every pixel they
                        took came out of the duration, which truncated, and then out of the clock,
                        which wrapped under itself. */}
                    <div className="od-rail-item-line od-rail-item-marks">
                        {types.length > 0 && (
                            <span className="od-rail-item-dots" title={summariseEvents(types)}>
                                {types.slice(0, MAX_DOTS).map((type, dot) => (
                                    <span
                                      // Events carry no id here and repeat by type, so the position
                                      // in the strip is the only stable key available.
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={`${type}-${dot}`}
                                      className="od-rail-dot"
                                      style={{ background: EVENT_COLOR[type] }}
                                    />
                                ))}
                            </span>
                        )}
                        <OdEcoPill score={trip.ecoScore} />
                    </div>

                    <div className="od-rail-item-route">
                        <span className="od-rail-item-place" title={trip.origin || UNKNOWN_PLACE}>
                            <span className="od-rail-place-dot od-rail-place-from" />
                            {/* The label needs its own box to ellipsis in: text-overflow has no
                                effect on a bare text node inside a flex container. */}
                            <span className="od-rail-place-text">{trip.origin || UNKNOWN_PLACE}</span>
                        </span>
                        <span className="od-rail-item-arrow">→</span>
                        <span className="od-rail-item-place" title={trip.destination || UNKNOWN_PLACE}>
                            <span className="od-rail-place-dot od-rail-place-to" />
                            <span className="od-rail-place-text">{trip.destination || UNKNOWN_PLACE}</span>
                        </span>
                    </div>
                </button>
            </div>
        );
    }, [rows, eventsByTrip, selectedTripId, onSelectTrip, onHoverTrip, zone]);

    const filtered = typeof totalCount === 'number' && totalCount !== list.length;

    return (
        <div className="od-rail">
            <div className="od-rail-head">
                <span className="od-rail-title">Trips</span>
                <span className="od-badge od-badge-neutral">
                    {filtered ? `${list.length} of ${totalCount}` : `${list.length}`}
                </span>
            </div>
            <div className="od-rail-list" ref={listWrapRef}>
                {list.length === 0 ? (
                    <div className="od-empty">
                        <div className="od-empty-text">
                            No trips match the current filters.
                        </div>
                    </div>
                ) : (
                    size.height > 0 && (
                        <VariableSizeList
                          ref={listRef}
                          height={size.height}
                          width={size.width}
                          itemCount={rows.length}
                          itemSize={itemSize}
                          estimatedItemSize={TRIP_HEIGHT}
                          overscanCount={4}
                        >
                            {renderRow}
                        </VariableSizeList>
                    )
                )}
            </div>
        </div>
    );
};

export default TripRail;
